/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CalendarClock, CheckCircle2, Clock, Coins, Download, RefreshCw, Sprout } from 'lucide-react';
import { fetchDividends } from '../store/slices/portfolioSlice';
import { exportDividends } from '../services/csvExport';
import { BrokerGate } from '../components/shell/Broker';
import { MonthlyBars } from '../components/charts';
import { FutureIncome } from '../components/plan/FutureIncome';
import { formatDate, formatNumber } from '../theme/format';
import {
  Badge, Button, Card, CardHeader, EmptyState, IconTile, Money, PageHeader, Select, SkeletonTable, SymbolMark, Table, Tabs,
  TBody, Td, Th, THead, Tr,
} from '../components/ui';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

const TABS = [
  { id: 'incoming', label: 'À venir', icon: Clock, tabId: 'dividends-tab-incoming', panelId: 'dividends-panel' },
  { id: 'paid', label: 'Versés', icon: CheckCircle2, tabId: 'dividends-tab-paid', panelId: 'dividends-panel' },
  { id: 'future', label: 'Revenus futurs', icon: Sprout, tabId: 'dividends-tab-future', panelId: 'dividends-panel' },
];

const monthIndex = (value) => {
  const match = /^\d{4}-(\d{2})/.exec(value || '');
  return match ? Number(match[1]) - 1 : new Date(value).getMonth();
};

const MONTHS = Array.from({ length: 12 }, (_, i) => formatDate(`2026-${String(i + 1).padStart(2, '0')}-01`, 'month'));

const Amount = ({ tx, estimated, className }) => (
  <span className={className} title={estimated ? 'Montant estimé' : undefined}>
    {estimated && <span aria-hidden="true" className="text-gain-400">~</span>}
    <Money value={tx.BookedAmount || 0} sign={!estimated} className="font-semibold text-gain-400" />
  </span>
);

const DividendList = ({ transactions, incoming }) => (
  <>
    <ul className="space-y-3 md:hidden">
      {transactions.map((tx, index) => (
        <li key={tx.BookingId || index} className="rounded-control border border-line bg-surface-2/50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <SymbolMark symbol={tx.Instrument?.Symbol} />
              <div className="min-w-0">
                <p className="truncate font-medium text-fg">{tx.Instrument?.Symbol || 'Inconnu'}</p>
                <p className="truncate text-xs text-fg-subtle">{tx.Instrument?.Description || '—'}</p>
              </div>
            </div>
            <Amount tx={tx} estimated={incoming} className="text-sm" />
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="eyebrow">Type</dt>
              <dd className="mt-1 text-fg-body">{tx.EventDisplay || tx.Event || '—'}</dd>
            </div>
            <div className="text-right">
              <dt className="eyebrow">{incoming ? 'Détachement' : 'Date'}</dt>
              <dd className="num mt-1 text-fg-body">{formatDate(tx.Date)}</dd>
            </div>
            {incoming && (
              <>
                <div>
                  <dt className="eyebrow">Quantité</dt>
                  <dd className="num mt-1 text-fg-body">{formatNumber(tx.EligibleHoldings)}</dd>
                </div>
                <div className="text-right">
                  <dt className="eyebrow">Paiement</dt>
                  <dd className="num mt-1 text-fg-body">{formatDate(tx.PayDate)}</dd>
                </div>
              </>
            )}
          </dl>
        </li>
      ))}
    </ul>

    <Table containerClassName="hidden md:block">
      <THead>
        <tr>
          <Th>Valeur</Th>
          <Th>Type</Th>
          {incoming && <Th align="right">Quantité</Th>}
          <Th align="right">{incoming ? 'Montant estimé' : 'Montant'}</Th>
          <Th align="right">{incoming ? 'Date de détachement' : 'Date'}</Th>
          {incoming && <Th align="right">Date de paiement</Th>}
        </tr>
      </THead>
      <TBody>
        {transactions.map((tx, index) => (
          <Tr key={tx.BookingId || index}>
            <Td className="w-full max-w-0">
              <div className="flex items-center gap-3">
                <SymbolMark symbol={tx.Instrument?.Symbol} />
                <div className="min-w-0">
                  {tx.Instrument?.Symbol
                    ? <Link to={`/stock/${encodeURIComponent(tx.Instrument.Symbol)}`} className="block truncate font-medium text-fg transition-colors hover:text-gold-200">{tx.Instrument.Symbol}</Link>
                    : <span className="block font-medium text-fg">Inconnu</span>}
                  <p className="truncate text-xs text-fg-subtle">{tx.Instrument?.Description || '—'}</p>
                </div>
              </div>
            </Td>
            <Td><Badge>{tx.EventDisplay || tx.Event || '—'}</Badge></Td>
            {incoming && <Td align="right" numeric>{formatNumber(tx.EligibleHoldings)}</Td>}
            <Td align="right"><Amount tx={tx} estimated={incoming} /></Td>
            <Td align="right" numeric className="whitespace-nowrap">{formatDate(tx.Date)}</Td>
            {incoming && <Td align="right" numeric className="whitespace-nowrap">{formatDate(tx.PayDate)}</Td>}
          </Tr>
        ))}
      </TBody>
    </Table>
  </>
);

const Dividends = () => {
  const dispatch = useDispatch();
  const { dividends, dividendsLoading, saxoConnected } = useSelector((state) => state.portfolio);
  const [activeTab, setActiveTab] = useState('paid');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const incoming = activeTab === 'incoming';
  const future = activeTab === 'future';

  useEffect(() => {
    if (saxoConnected && !future) {
      dispatch(fetchDividends({ tab: activeTab, year: activeTab === 'paid' ? selectedYear : undefined }));
    }
  }, [dispatch, saxoConnected, activeTab, selectedYear, future]);

  const handleRefresh = () => {
    dispatch(fetchDividends({ tab: activeTab, year: activeTab === 'paid' ? selectedYear : undefined }));
  };

  const transactions = incoming ? dividends.incoming : dividends.paid;
  const total = useMemo(() => transactions.reduce((sum, tx) => sum + (tx.BookedAmount || 0), 0), [transactions]);
  const monthly = useMemo(() => {
    const sums = new Array(12).fill(0);
    transactions.forEach((tx) => {
      const month = monthIndex(tx.Date);
      if (month >= 0 && month < 12) sums[month] += tx.BookedAmount || 0;
    });
    return sums.map((value, i) => ({ label: MONTHS[i], value: Math.round(value * 100) / 100 }));
  }, [transactions]);
  const ready = !dividendsLoading && transactions.length > 0;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Patrimoine"
        title="Dividendes"
        description="Versements reçus et détachements programmés sur vos positions."
        actions={saxoConnected && (
          <>
            <Button variant="secondary" leadingIcon={Download} disabled={future || transactions.length === 0} onClick={() => exportDividends(transactions, incoming ? 'a-venir' : String(selectedYear))}>
              Exporter CSV
            </Button>
            <Button variant="secondary" leadingIcon={RefreshCw} loading={dividendsLoading} disabled={future} onClick={handleRefresh}>
              Actualiser
            </Button>
          </>
        )}
      />
      <BrokerGate>
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs variant="pill" ariaLabel="Dividendes" items={TABS} value={activeTab} onChange={setActiveTab} className="self-start" />
            {activeTab === 'paid' && (
              <Select aria-label="Année" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="w-full sm:w-36">
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </Select>
            )}
          </div>

          <div role="tabpanel" id="dividends-panel" aria-labelledby={`dividends-tab-${activeTab}`} className="space-y-6">
            {future ? <FutureIncome /> : (
            <>
            {ready && (
              <Card tone="gold" className="flex flex-wrap items-center gap-x-6 gap-y-4">
                <IconTile icon={incoming ? CalendarClock : Coins} size="lg" />
                <div className="min-w-0">
                  <p className="eyebrow">{incoming ? 'Total attendu' : `Total ${selectedYear}`}</p>
                  <p className="mt-1 font-display text-display-md text-fg">
                    {incoming && <span aria-hidden="true" className="text-fg-muted">~</span>}
                    <Money value={total} />
                  </p>
                </div>
                <p className="num text-sm text-fg-muted sm:ml-auto">
                  {transactions.length} {transactions.length > 1 ? 'versements' : 'versement'}
                </p>
              </Card>
            )}

            {ready && !incoming && (
              <Card>
                <CardHeader title="Répartition mensuelle" description={`Dividendes versés en ${selectedYear}`} />
                <MonthlyBars data={monthly} height={220} />
              </Card>
            )}

            <Card>
              {dividendsLoading ? (
                <div role="status">
                  <span className="sr-only">Chargement des dividendes…</span>
                  <SkeletonTable rows={5} cols={4} />
                </div>
              ) : transactions.length === 0 ? (
                <EmptyState
                  icon={Coins}
                  title={incoming ? 'Aucun dividende à venir' : `Aucun dividende versé en ${selectedYear}`}
                  description={incoming ? 'Les prochains dividendes apparaissent dès que votre courtier les a programmés.' : undefined}
                />
              ) : (
                <DividendList transactions={transactions} incoming={incoming} />
              )}
            </Card>
            </>
            )}
          </div>
        </div>
      </BrokerGate>
    </div>
  );
};

export default Dividends;
